import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  Image,
  List,
  ListItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {colors, useCartContext} from 'components/providers';
import {Edit2} from 'lucide-react';
import {FC, useEffect, useMemo} from 'react';
import {TranslationKeys} from 'translations';
import {
  INightTimeAddedTerms,
  IOrder,
  IPreOrderData,
  IRequiredProduct,
  IRestaurantDistancePriceResponse,
  IRestaurantResponse,
} from 'types';

import {useCartStore} from '../../../store/cartStore';
import {calculateNightPrice} from '../../../utils/cartUtils';
import {calcSmallOrderFee} from '../../../utils/delivery';
import logger from '../../../utils/logger';
import {CartPromoText} from '../CartPromoText';
import {DeliverySmallOrderFeeModal} from '../DeliverySmallOrderFeeModal';
import {Price} from '../price';
import {SmallOrderFeeRow} from '../SmallOrderFeeRow';
import {
  CostDelivery,
  CostOfProducts,
  CostPromoCode,
  CostRequiredProducts,
  CostSurcharge,
  NightSurcharge,
  OrderTotalListItem,
} from './components';

interface Props {
  cartItemsPrice: number;
  deliveryTime?: string;
  voucher?: any;
  deliveryDetails: {
    deliveryAmount: number;
  };
  t: (term: TranslationKeys) => string;
  hasPreorder?: boolean;
  requiredProducts?: IRequiredProduct[];
  dynamicDeliveryPrice: IRestaurantDistancePriceResponse;
  nighttimeAddedTerms?: INightTimeAddedTerms;
  preorder?: IPreOrderData;
  orderNighttime_added_price?: number;
  difficult_delivery_surcharge?: number;
  difficult_delivery_reason?: string;
  isOrderCreated?: boolean;
  order?: IOrder;
  showCartButton?: boolean;
  provider?: IRestaurantResponse;
}

export const OrderTotalDetails: FC<Props> = ({
  t,
  cartItemsPrice,
  deliveryDetails,
  deliveryTime,
  requiredProducts,
  hasPreorder,
  dynamicDeliveryPrice,
  voucher,
  nighttimeAddedTerms,
  preorder,
  orderNighttime_added_price,
  difficult_delivery_surcharge,
  difficult_delivery_reason,
  isOrderCreated,
  order,
  showCartButton = false,
  provider,
}) => {
  const {deliveryAmount} = deliveryDetails;
  const {surcharge_for_out_area, surcharge} = dynamicDeliveryPrice;

  const difficultDelivery = difficult_delivery_surcharge ?? 0;

  const nightPrice =
    orderNighttime_added_price !== undefined
      ? orderNighttime_added_price
      : calculateNightPrice(nighttimeAddedTerms, preorder);
  const totalPrice =
    cartItemsPrice +
    (deliveryAmount ?? 0) +
    surcharge_for_out_area +
    surcharge -
    (voucher?.reduction ?? 0) +
    nightPrice +
    difficultDelivery;

  const {cartProvider, cartItems, setSideCartOpen} = useCartContext();
  const {setProvider} = useCartStore();
  const isMetro = cartProvider?.slug === 'metro';
  const {isOpen: isOpenInfo, onOpen: onOpenInfo, onClose: onCloseInfo} = useDisclosure();
  const deliveryPrice = cartProvider?.delivery_price;

  const {fee: smallOrderFee, isFeeApplied} = useMemo(
    () =>
      calcSmallOrderFee({
        tiers: deliveryPrice,
        cartSubtotal: cartItemsPrice,
        currentDeliveryAmount: deliveryAmount ?? 0,
      }),
    [deliveryPrice, cartItemsPrice, deliveryAmount],
  );

  useEffect(() => {
    if (provider) {
      setProvider(provider);
    }
  }, [provider, setProvider]);

  useEffect(() => {
    if (isOrderCreated && cartItemsPrice >= 200 && isFeeApplied) {
      logger.error(new Error('order data'), order);
    }
  }, [cartItemsPrice, isFeeApplied, isOrderCreated, order]);

  return (
    <Container variant="white-block" w="100%" p="30px 16px" borderRadius="0px 0px 16px 16px" position="relative">
      <Image src="/border.svg" alt="" position="absolute" top={['-8px', '-9px']} left="0" right="0" width="100%" />
      <Box display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #E6E6E6" pb="15px">
        <Heading as="h4" fontWeight="500" fontSize="18px" color={colors.textGray}>
          {t('ORDER_SUMMARY')}
        </Heading>
        {/* Cart Button - only show in checkout */}
      </Box>
      {showCartButton && (
        <>
          <Flex alignItems="center" justifyContent="space-between" w="100%" mt={3}>
            <Button
              variant="solid"
              height="32px"
              fontSize="14px"
              fontWeight="500"
              borderColor={colors.borderGray}
              flex={1}
              _hover={{
                borderColor: colors.black,
                bg: colors.lightGray,
              }}
              disabled={cartItems.length === 0}
              onClick={() => setSideCartOpen(true)}
              rightIcon={<Edit2 size={14} />}
              justifyContent="space-between"
            >
              {t('CART')} {`(${cartItems.length})`}
            </Button>
          </Flex>
          <Divider mt={3} />
        </>
      )}
      <List fontSize={['12px', '18px']}>
        <CostOfProducts cartItemsPrice={cartItemsPrice} t={t} />
        <CartPromoText />
        <CostDelivery
          deliveryAmount={deliveryAmount - (isFeeApplied ? smallOrderFee : 0)}
          deliveryTime={deliveryTime ?? ''}
          dynamicDeliveryPrice={dynamicDeliveryPrice}
          hasPreorder={hasPreorder}
          cartItemsPrice={cartItemsPrice}
          t={t}
          isMetro={isMetro}
        />
        <SmallOrderFeeRow fee={smallOrderFee} onOpenInfo={onOpenInfo} t={t} />
        {difficultDelivery > 0 ? (
          <OrderTotalListItem boxProps={{borderBottom: '1px solid #E6E6E6', textAlign: 'left'}} title="">
            {difficult_delivery_reason ? (
              <Text
                flex={1}
                as="span"
                fontWeight="500"
                fontSize={['14px', '18px']}
                textAlign="left"
                alignSelf="flex-start"
              >
                {difficult_delivery_reason}
              </Text>
            ) : null}

            <Box>
              <Price
                value={difficultDelivery}
                priceProps={{
                  fontWeight: '500',
                }}
              />
            </Box>
          </OrderTotalListItem>
        ) : null}

        <NightSurcharge price={nightPrice} range={nighttimeAddedTerms?.hours_range} />
        {!!surcharge_for_out_area && (
          <CostSurcharge dynamicDeliveryPrice={dynamicDeliveryPrice} deliveryAmount={deliveryAmount} t={t} />
        )}
        <CostPromoCode voucher={voucher} />
        {requiredProducts && <CostRequiredProducts requiredProducts={requiredProducts} />}
        <ListItem display="flex" justifyContent="space-between" pt="15px">
          <Text as="span" fontWeight={700}>
            {t('TOTAL_ORDER')}
          </Text>
          <Price
            value={totalPrice}
            priceProps={{
              fontWeight: '700',
            }}
          />
        </ListItem>
      </List>
      {isOpenInfo && <DeliverySmallOrderFeeModal isOpen={isOpenInfo} onClose={onCloseInfo} />}
    </Container>
  );
};
